import {
  emptyWhatsAppTemplateContent,
  whatsAppTemplateContentSchema,
  type WhatsAppTemplateContent,
} from "./whatsapp-template-content";

export const WHATSAPP_TEMPLATE_CATEGORIES = [
  "MARKETING",
  "UTILITY",
  "AUTHENTICATION",
] as const;

export type WhatsAppTemplateCategory =
  (typeof WHATSAPP_TEMPLATE_CATEGORIES)[number];

export function isWhatsAppTemplateCategory(
  value: string,
): value is WhatsAppTemplateCategory {
  return (WHATSAPP_TEMPLATE_CATEGORIES as readonly string[]).includes(value);
}

export const WHATSAPP_TEMPLATE_CATEGORY_LABELS: Record<
  WhatsAppTemplateCategory,
  string
> = {
  MARKETING: "Marketing",
  UTILITY: "Utility",
  AUTHENTICATION: "Authentication",
};

/**
 * The one status Hightouch owns: the template exists here and has never been
 * sent to the provider, or the provider content it had has been deleted.
 * Everything else in this column is the provider's own verdict.
 */
export const WHATSAPP_TEMPLATE_STATUS_UNSUBMITTED = "UNSUBMITTED";

/**
 * Meta's own template statuses.
 */
export const META_TEMPLATE_STATUSES = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "PAUSED",
  "DISABLED",
  "DELETED",
  "PENDING_DELETION",
  "LOCKED",
  "LIMIT_EXCEEDED",
  "IN_APPEAL",
  "FLAGGED",
  "REINSTATED",
  // TODO
  // "ARCHIVED",
  // "UNARCHIVED",
] as const;

export type WhatsAppTemplateStatus =
  | typeof WHATSAPP_TEMPLATE_STATUS_UNSUBMITTED
  | (typeof META_TEMPLATE_STATUSES)[number];

export function isWhatsAppTemplateStatus(
  value: string,
): value is WhatsAppTemplateStatus {
  return (
    value === WHATSAPP_TEMPLATE_STATUS_UNSUBMITTED ||
    (META_TEMPLATE_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * The statuses Meta still delivers on. REINSTATED restores a template Meta had
 * suppressed and FLAGGED is a quality warning that does not stop delivery, so
 * both send exactly as APPROVED does.
 *
 * Typed as strings rather than `WhatsAppTemplateStatus` because the column has
 * no CHECK and stores provider vocabulary verbatim: a value can reach it
 * before it reaches the union above.
 */
export const WHATSAPP_SENDABLE_STATUSES: readonly string[] = [
  "APPROVED",
  "REINSTATED",
  "FLAGGED",
];

/**
 * Membership rather than "not one of the bad ones": a status nobody has taught
 * us about refuses the send, which is the safe direction while Meta keeps
 * extending its vocabulary.
 */
export function isSendableWhatsAppTemplateStatus(
  status: string | null | undefined,
): boolean {
  return status != null && WHATSAPP_SENDABLE_STATUSES.includes(status);
}

export const DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE = "en_US";

export const DEFAULT_WHATSAPP_TEMPLATE_CATEGORY: WhatsAppTemplateCategory =
  "MARKETING";

/** The authored components, or an empty template when the row will not parse. */
export function readWhatsAppEditorContent(
  editorState: unknown,
): WhatsAppTemplateContent {
  const parsed = whatsAppTemplateContentSchema.safeParse(editorState);
  return parsed.success ? parsed.data : emptyWhatsAppTemplateContent();
}

/**
 * A locale's compiled variable map: the Meta position each variable occupies
 * in that locale's approved template, against the Liquid that fills it.
 *
 * Compiler output, frozen alongside `compiled_components` and
 * `provider_content_id`, so the numbering always matches the template Meta
 * approved rather than whatever the body says now. Positions are per locale:
 * a translation that reorders a sentence renumbers them.
 *
 * Null for anything that is not a flat map of strings, including a single
 * non-string value. Dropping the bad entry and keeping the rest would send an
 * approved template with an unfilled hole, which is a message the author never
 * saw; `{}` stays valid, since a template can legitimately declare no holes.
 */
export function readWhatsAppTemplateVariables(
  variables: unknown,
): Record<string, string> | null {
  if (
    variables === null ||
    typeof variables !== "object" ||
    Array.isArray(variables)
  ) {
    return null;
  }
  const entries = Object.entries(variables);
  if (entries.some(([, value]) => typeof value !== "string")) return null;
  return Object.fromEntries(entries) as Record<string, string>;
}

export function contentIsNewerThanSubmission({
  contentUpdatedAt,
  submittedAt,
}: {
  contentUpdatedAt: Date | string | null | undefined;
  submittedAt: Date | string | null | undefined;
}): boolean {
  if (!contentUpdatedAt || !submittedAt) return false;
  return new Date(contentUpdatedAt) > new Date(submittedAt);
}
